import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { FormPageLayout } from '../../../../components/layout/FormPageLayout';
import { Input, FormField } from '../../../../components/ui/FormField';
import { FormSelect } from '../../../../components/ui/FormSelect';
import { useCreateReceiptVoucher } from '../../../../features/finance/hooks';
import { useToast } from '../../../../components/ui/Toast';
import { Decimal } from 'decimal.js';
import { z } from 'zod';
import { Banner } from '../../../../components/ui/Banner';
import { CustomerPicker } from '../../../../features/customers/components/CustomerPicker';

export const Route = createFileRoute('/_auth/finance/receipt-vouchers/new')({
  component: CreateReceiptVoucherPage,
});

const CATEGORY_NAMES: Record<string, string> = {
  membership_fees: 'Membership Fees',
  application_fees: 'Application Fees',
  interest_advances: 'Interest on Advances',
  misc_income: 'Miscellaneous Income',
  asset_sales: 'Sale of Office Assets',
  advance_refund: 'Short-term Advance Refund',
};

const voucherSchema = z.object({
  depositorName: z.string().min(1, 'Depositor name is required'),
  customerId: z.string().optional(),
  category: z.string().min(1, 'Category is required'),
  amountRupees: z.string().min(1, 'Amount is required'),
  paymentMode: z.enum(['CASH', 'CHEQUE', 'UPI', 'NEFT']),
  referenceNumber: z.string().optional(),
  narration: z.string().min(5, 'Narration must be at least 5 characters long'),
});

type VoucherForm = z.infer<typeof voucherSchema>;

const voucherFormConfig = defineFormPage<VoucherForm>({
  resource: 'receipt-vouchers',
  defaultValues: {
    depositorName: '',
    customerId: '',
    category: 'membership_fees',
    amountRupees: '',
    paymentMode: 'UPI' as const,
    referenceNumber: '',
    narration: '',
  },
  schema: voucherSchema,
});

function CreateReceiptVoucherPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createMutation = useCreateReceiptVoucher();

  const {
    form,
    isSubmitting,
    handleSubmit,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(voucherFormConfig, createMutation, {
    routeId: 'finance/receipt-vouchers/new',
    onBeforeSubmit: (value) => {
      const paise = new Decimal(value.amountRupees).mul(100).round().toNumber();
      return {
        depositorName: value.depositorName.trim(),
        customerId: value.customerId || undefined,
        category: value.category,
        amountPaise: paise,
        paymentMode: value.paymentMode as never,
        referenceNumber: value.referenceNumber?.trim() || undefined,
        notes: value.narration.trim(),
        entryDate: new Date().toISOString().split('T')[0],
      } as never;
    },
    onSuccess: () => {
      toast({
        type: 'success',
        title: 'Voucher Recorded',
        description: 'Successfully recorded receipt voucher.',
      });
      void navigate({ to: '/finance/receipt-vouchers' });
    },
    onError: (err: { message?: string }) => {
      toast({
        type: 'error',
        title: 'Failed to Record Voucher',
        description: err?.message || 'Voucher recording failed. Verify all required fields and try again.',
      });
    },
  });

  return (
    <FormPageLayout
      title="Record Miscellaneous Receipt Voucher"
      subtitle="Create a non-subscription voucher recording credit onto institutional general ledger accounts."
      backTo="/finance/receipt-vouchers"
      variant="card"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => {
                    clearDraft();
                  },
                }}
              />
            )}

            <form.Field
              name="depositorName"
              validators={{ onChange: ({ value }: { value: any }) => voucherSchema.shape.depositorName.safeParse(value).success ? undefined : voucherSchema.shape.depositorName.safeParse(value).error?.issues[0]?.message }}
              children={(field: any) => (
                <FormField label="Depositor / Paid By Name" required>
                  <Input
                    type="text"
                    placeholder="e.g. Ramesh Kumar"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="customerId"
                children={(field: any) => (
                  <FormField label="Linked Customer (Optional)">
                    <CustomerPicker
                      value={field.state.value}
                      onChange={field.handleChange}
                      placeholder="Search customer..."
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />

              <form.Field
                name="category"
                children={(field: any) => (
                  <FormSelect
                    label="Ledger Credit Category"
                    required
                    options={Object.entries(CATEGORY_NAMES).map(([key, value]) => ({
                      value: key,
                      label: value,
                    }))}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="amountRupees"
                validators={{ onChange: ({ value }: { value: any }) => voucherSchema.shape.amountRupees.safeParse(value).success ? undefined : voucherSchema.shape.amountRupees.safeParse(value).error?.issues[0]?.message }}
                children={(field: any) => (
                  <FormField label="Amount in Rupees" required>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="0.00"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />

              <form.Field
                name="paymentMode"
                children={(field: any) => (
                  <FormSelect
                    label="Payment Mode"
                    required
                    options={[
                      { value: 'UPI', label: 'UPI / Online' },
                      { value: 'CASH', label: 'Cash' },
                      { value: 'NEFT', label: 'Bank Transfer / NEFT' },
                      { value: 'CHEQUE', label: 'Cheque' },
                    ]}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value as never)}
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>

            <form.Field
              name="referenceNumber"
              children={(field: any) => {
                const show = form.state.values?.paymentMode !== 'CASH';
                if (!show) return null;
                return (
                  <FormField label="Reference / Instrument No." required>
                    <Input
                      type="text"
                      placeholder="e.g. UPI transaction ID or Cheque No."
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                );
              }}
            />

            <form.Field
              name="narration"
              validators={{ onChange: ({ value }: { value: any }) => voucherSchema.shape.narration.safeParse(value).success ? undefined : voucherSchema.shape.narration.safeParse(value).error?.issues[0]?.message }}
              children={(field: any) => (
                <FormField label="Transaction Narration / Description" required>
                  <textarea
                    className="w-full min-h-[70px] p-2 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500 placeholder:text-text-muted focus:ring-1 focus:ring-accent-500"
                    placeholder="Required description of cash/bank credit entry details..."
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            />

            <FormActions onCancel={() => void navigate({ to: '/finance/receipt-vouchers' })} isSubmitting={isSubmitting} submitLabel="Create Voucher" />
          </form>
    </FormPageLayout>
  );
}
